import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Hash, Archive, CheckCircle2, AlertTriangle } from 'lucide-react'
import { numberPoolQueryOptions } from '@/utils/numberPoolQueryOptions'
import { archiveGroupChat } from '@/utils/groupChatQueryOptions'
import type { PoolChat, PoolNumber } from '@/types/numberPoolTypes'
import { useShellStore } from '@/stores/shellStore'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

/** "+12367608694" → "+1 236 760 8694", left alone if it isn't a NANP number. */
function prettyNumber(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164)
  return m ? `+1 ${m[1]} ${m[2]} ${m[3]}` : e164
}

/** "3 days ago" / "today" — enough to spot the obvious archive candidate. */
function quietFor(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const days = Math.floor((Date.now() - then) / 86_400_000)
  if (days <= 0) return 'active today'
  if (days === 1) return 'quiet 1 day'
  return `quiet ${days} days`
}

/**
 * Super-admin view of the Twilio number pool. A chat can only be created when
 * some number has no live chat containing its people, so when creation is
 * refused this is the screen that explains why and lets you fix it.
 *
 * Ordinary admins can't see every chat, which means capacity can be consumed by
 * chats they'll never find — hence super-admin only, matching the backend.
 */
export function NumberPoolDialog() {
  const open = useShellStore((s) => s.numberPoolOpen)
  const close = useShellStore((s) => s.closeNumberPool)
  const selectConversation = useShellStore((s) => s.selectConversation)
  const queryClient = useQueryClient()

  const [pendingArchive, setPendingArchive] = useState<PoolChat | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data, status } = useQuery({ ...numberPoolQueryOptions(), enabled: open })
  const numbers = data?.numbers ?? []

  const archiveMutation = useMutation({
    mutationFn: (chatId: string) => archiveGroupChat(chatId),
    onSuccess: () => {
      // The freed number should show as free straight away, and the rail must
      // drop the archived chat.
      queryClient.invalidateQueries({ queryKey: ['numberPool'] })
      queryClient.invalidateQueries({ queryKey: ['groupChats'] })
      setPendingArchive(null)
      setError(null)
    },
    onError: (e: Error) => {
      setError(e.message)
      setPendingArchive(null)
    },
  })

  const active = numbers.filter((n) => n.isActive)
  const free = active.filter((n) => n.chats.length === 0).length
  const retired = numbers.filter((n) => !n.isActive && n.chats.length > 0)

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[10px] bg-primary-tint text-primary">
              <Hash className="h-[18px] w-[18px]" />
            </span>
            <div className="min-w-0">
              <DialogTitle>Group chat numbers</DialogTitle>
              <DialogDescription className="mt-0.5 text-[12px]">
                A number can host several chats, but only one per person. Archive a chat to free
                its number.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {status === 'pending' ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : status === 'error' ? (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            Couldn't load the number pool.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-[13px] text-muted-foreground">
              {active.length} number{active.length === 1 ? '' : 's'} in the pool ·{' '}
              <span className={cn('font-semibold', free > 0 ? 'text-sage-ink' : 'text-warn-ink')}>
                {free > 0 ? `${free} with no chat` : 'every number has a chat on it'}
              </span>
            </div>

            {active.map((n) => (
              <NumberCard
                key={n.phoneNumber}
                number={n}
                onOpenChat={(chatId) => {
                  close()
                  selectConversation(chatId, 'chat')
                }}
                onArchive={setPendingArchive}
              />
            ))}

            {retired.length > 0 && (
              <div className="space-y-3 pt-1">
                <div className="text-[12px] font-bold uppercase tracking-[0.03em] text-muted-foreground">
                  Retired numbers still holding chats
                </div>
                <p className="text-[12px] text-muted-foreground">
                  These are out of the pool, so nothing new is assigned to them — but the chats
                  below still hold their Twilio bindings until archived.
                </p>
                {retired.map((n) => (
                  <NumberCard
                    key={n.phoneNumber}
                    number={n}
                    onOpenChat={(chatId) => {
                      close()
                      selectConversation(chatId, 'chat')
                    }}
                    onArchive={setPendingArchive}
                  />
                ))}
              </div>
            )}

            {error && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>
        )}

        <AlertDialog
          open={pendingArchive !== null}
          onOpenChange={(o) => !o && setPendingArchive(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Archive "{pendingArchive?.name}"?</AlertDialogTitle>
              <AlertDialogDescription>
                The chat is removed from the list and its Twilio conversation deleted, which
                releases its number. Participants can no longer reply to it. This can't be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => pendingArchive && archiveMutation.mutate(pendingArchive.id)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {archiveMutation.isPending ? 'Archiving…' : 'Archive'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  )
}

function NumberCard({
  number,
  onOpenChat,
  onArchive,
}: {
  number: PoolNumber
  onOpenChat: (chatId: string) => void
  onArchive: (chat: PoolChat) => void
}) {
  const busy = number.chats.length > 0

  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <div className="flex items-center justify-between gap-2 border-b border-border-2 bg-surface-2 px-3.5 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[13.5px] font-bold text-foreground">
            {prettyNumber(number.phoneNumber)}
          </span>
          {number.isLastResort && (
            <span className="flex-none rounded-[5px] bg-warn-tint px-1.5 py-0.5 text-[9.5px] font-extrabold uppercase tracking-[0.04em] text-warn-ink">
              Last resort
            </span>
          )}
          {!number.isActive && (
            <span className="flex-none rounded-[5px] bg-surface px-1.5 py-0.5 text-[9.5px] font-extrabold uppercase tracking-[0.04em] text-faint">
              Retired
            </span>
          )}
        </div>
        {busy ? (
          <span className="flex flex-none items-center gap-1.5 text-[11.5px] font-semibold text-muted-foreground">
            {number.chats.length} chat{number.chats.length === 1 ? '' : 's'}
          </span>
        ) : (
          <span className="flex flex-none items-center gap-1.5 text-[11.5px] font-semibold text-sage-ink">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Free
          </span>
        )}
      </div>

      {number.chats.length === 0 ? (
        <div className="px-3.5 py-3 text-[12.5px] text-faint">
          No chats — available for the next one.
        </div>
      ) : (
        number.chats.map((c) => (
          <div
            key={c.id}
            className="flex items-center gap-2 border-b border-border-2 px-3.5 py-2.5 last:border-b-0"
          >
            <button
              type="button"
              onClick={() => onOpenChat(c.id)}
              className="min-w-0 flex-1 text-left"
            >
              <div className="truncate text-[13px] font-semibold text-foreground hover:underline">
                {c.name || 'Unnamed chat'}
              </div>
              <div className="mt-px truncate text-[11.5px] text-muted-foreground">
                {c.participantCount} {c.participantCount === 1 ? 'person' : 'people'} ·{' '}
                {quietFor(c.lastActivity)}
              </div>
            </button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onArchive(c)}
              className="flex-none gap-1.5 text-destructive"
            >
              <Archive className="h-3.5 w-3.5" />
              Archive
            </Button>
          </div>
        ))
      )}
    </div>
  )
}

/** Shown in the composer/new-chat flows when the pool is full. */
export function PoolFullHint() {
  const openNumberPool = useShellStore((s) => s.openNumberPool)
  return (
    <button
      type="button"
      onClick={openNumberPool}
      className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-primary hover:underline"
    >
      <AlertTriangle className="h-3.5 w-3.5" />
      Manage group chat numbers
    </button>
  )
}
